import * as React from 'react'
import type { TenantMode } from '@lib/enums/tenant-mode.enum'
import type { ViewerAudience } from '@lib/enums/viewer-audience.enum'
import type { TenantRole } from '@lib/enums/tenant-role.enum'
import type { Uuid } from '@lib/validators/params'
import type { ApiClient } from '@/helpers/apiClient'

/** Why the dashboard is read-only, when it is. */
export type ReadOnlyReason = 'suspended' | 'view-as'

interface DashboardScopeCommon {
  /** Parsed organization id. A route param or other plain string has to go through `uuidSchema` first. */
  tenantId: Uuid
  tenantName: string
  /** The session's API client: the tenant token, or the staff token for view-as. */
  client: ApiClient
  /**
   * Which world the dashboard shows: live, or test (separate keys, settings,
   * and envelopes). A view choice; the API scopes every request by it.
   */
  mode: TenantMode
  setMode: (mode: TenantMode) => void
  /** Base path for dashboard links (`` for the dashboard, `/ops/tenants/:id/view` for view-as). */
  basePath: string
}

/** A tenant user's own session. `refreshSession` re-reads it after their membership changes. */
type TenantDashboardScope = DashboardScopeCommon & {
  cacheKey: Extract<ViewerAudience, 'tenant'>
  role: TenantRole
  currentUserId: Uuid
  refreshSession: () => Promise<void>
} & (
  | { readOnly: false; readOnlyReason: null }
  | { readOnly: true; readOnlyReason: Extract<ReadOnlyReason, 'suspended'> }
)

/** Staff viewing the tenant's dashboard. The API refuses writes; the role is owner so the pages show the owner's view. */
type ViewAsDashboardScope = DashboardScopeCommon & {
  cacheKey: Extract<ViewerAudience, 'staff'>
  role: 'owner'
  currentUserId: null
  readOnly: true
  readOnlyReason: Extract<ReadOnlyReason, 'view-as'>
}

/**
 * Everything a dashboard page needs to know about who is looking at which
 * organization. Provided by `TenantLayout` (a tenant user's own session) and
 * by `OpsViewAsLayout` (staff viewing as the tenant), so the same pages serve
 * both without knowing which.
 *
 * The variants keep read-only, audience, and session fields together: a staff
 * view is always read-only and has no tenant session to refresh, and a
 * suspended organization is read-only for a real tenant user.
 */
export type DashboardScopeValue = TenantDashboardScope | ViewAsDashboardScope

/** Re-reads the tenant session. Staff view-as has no tenant session, so it returns null. */
export function refreshTenantSession(scope: DashboardScopeValue): Promise<void> | null {
  if (scope.cacheKey !== 'tenant') return null
  return scope.refreshSession()
}

const DashboardScopeContext = React.createContext<DashboardScopeValue | null>(null)

interface DashboardScopeProviderProps {
  value: DashboardScopeValue
  children: React.ReactNode
}

export function DashboardScopeProvider({ value, children }: DashboardScopeProviderProps) {
  return <DashboardScopeContext value={value}>{children}</DashboardScopeContext>
}

export function useDashboardScope(): DashboardScopeValue {
  const scope = React.use(DashboardScopeContext)
  if (scope === null) {
    throw new Error('useDashboardScope must be used within a DashboardScopeProvider')
  }
  return scope
}

/** Owners manage the team and read activity; only when the dashboard isn't read-only. */
export function canManageTeam(scope: DashboardScopeValue): boolean {
  return scope.role === 'owner' && !scope.readOnly
}

/** Owners buy and refund credit; never while suspended, and never staff viewing as the organization. */
export function canManageBilling(scope: DashboardScopeValue): boolean {
  return scope.role === 'owner' && !scope.readOnly
}

/** Keys in the mode on screen: live keys are owner-only; owners and developers manage test keys. */
export function canManageKeys(scope: DashboardScopeValue): boolean {
  if (scope.readOnly) return false
  return scope.mode === 'live' ? scope.role === 'owner' : scope.role === 'owner' || scope.role === 'developer'
}

/** Webhook URL, secret rotation, and embed origins: owners and developers. */
export function canEditSettings(scope: DashboardScopeValue): boolean {
  return (scope.role === 'owner' || scope.role === 'developer') && !scope.readOnly
}

/**
 * Owners and developers can reveal the webhook secret, even while suspended
 * (they may need it to verify deliveries still arriving). Never staff.
 */
export function canSeeSecrets(scope: DashboardScopeValue): boolean {
  return (scope.role === 'owner' || scope.role === 'developer') && scope.readOnlyReason !== 'view-as'
}

/** The organization profile (name, branding, timezone) is owner-only. */
export function canEditProfile(scope: DashboardScopeValue): boolean {
  return scope.role === 'owner' && !scope.readOnly
}
